<script>
  /**
   * Set to `true` to use the small version of the input
   * @type {boolean}
   */
  export let small = false;

  /**
   * Specify the input value
   * @type {number | string}
   */
  export let value = "";

  /**
   * Set to `true` to reveal the password
   * @type {boolean}
   */
  export let showPassword = false;

  /** Specify the placeholder text */
  export let placeholder = "";

  /** Specify the hide password label text */
  export let hidePasswordLabel = "Hide password";

  /** Specify the show password label text */
  export let showPasswordLabel = "Show password";

  /**
   * Set the alignment of the tooltip relative to the icon
   * @type {"start" | "center" | "end"}
   */
  export let tooltipAlignment = "center";

  /**
   * Set the position of the tooltip relative to the icon
   * @type {"top" | "right" | "bottom" | "left"}
   */
  export let tooltipPosition = "bottom";

  /** Set to `true` to disable the input */
  export let disabled = false;

  /** Specify the label text */
  export let labelText = "";

  /** Set to `true` to indicate an invalid state */
  export let invalid = false;

  /** Specify the text for the invalid state */
  export let invalidText = "";

  /** Set to `true` to use inline version */
  export let inline = false;

  /**
   * @type {boolean|null}
   * @default false
   */
  export let readonly = false;

  /**
   * @type {boolean|null}
   * @default false
   */
  export let required = false;

  /** Set an id for the input element */
  export let id = "ccs-" + Math.random().toString(36);

  /**
   * Specify a name attribute for the input
   * @type {string}
   */
  export let name = undefined;

  /** Obtain a reference to the input HTML element */
  export let ref = null;

  /**
   * @type {string?}
   */
  export let description = null;

  import { getContext } from "svelte";
  import WarningFilled from "../icons/WarningFilled.svelte";
  import View from "../icons/View.svelte";
  import ViewOff from "../icons/ViewOff.svelte";

  const ctx = getContext("Form");
  const removePreviousErrors = () => {
    if (invalid) {
      invalidText = "";
    }
    invalid = false; // reset invalid when the user clicks on the form element
  };

  $: type = showPassword ? "text" : "password";
  $: isFluid = !!ctx && ctx.isFluid;
  $: helperId = `helper-${id}`;
  $: errorId = `error-${id}`;
</script>

<!-- svelte-ignore a11y-mouse-events-have-key-events -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class:bx--form-item="{true}"
  class:bx--text-input-wrapper="{true}"
  class:bx--password-input-wrapper="{!isFluid}"
  class:bx--text-input-wrapper--inline="{inline}"
  on:click
  on:mouseover
  on:mouseenter
  on:mouseleave
>
  <label
    for="{id}"
    class:bx--label="{true}"
    class:bx--visually-hidden="{!labelText}"
    class:bx--label--disabled="{disabled}"
    class:bx--label--inline="{inline}"
    class:bx--label--inline--sm="{inline && small}"
  >
    <slot name="labelText">
      <p>{labelText}</p>
    </slot>
  </label>
  <div
    class:bx--text-input__field-outer-wrapper="{true}"
    class:bx--text-input__field-outer-wrapper--inline="{inline}"
  >
    <div
      class:bx--text-input__field-wrapper="{true}"
      data-invalid="{invalid || undefined}"
    >
      {#if invalid}
        <WarningFilled class="bx--text-input__invalid-icon" />
      {/if}
      <input
        bind:this="{ref}"
        data-invalid="{invalid || undefined}"
        aria-invalid="{invalid || undefined}"
        aria-describedby="{invalid
          ? errorId
          : description
          ? helperId
          : undefined}"
        id="{id}"
        name="{name}"
        placeholder="{placeholder}"
        type="{type}"
        value="{value ?? ''}"
        disabled="{disabled}"
        readonly="{readonly}"
        required="{required}"
        class:bx--text-input="{true}"
        class:bx--password-input="{true}"
        class:bx--text-input--invalid="{invalid}"
        class:bx--text-input--sm="{small}"
        {...$$restProps}
        on:change
        on:input
        on:input="{({ target }) => {
          value = target.value;
        }}"
        on:click="{removePreviousErrors}"
        on:keydown
        on:keyup
        on:focus="{removePreviousErrors}"
        on:blur
        on:paste
      />
      {#if isFluid && invalid && invalidText}
        <hr class="bx--text-input__divider" />
        <p class="bx--form-requirement" id="{errorId}">
          {invalidText}
        </p>
      {/if}
      {#if !(isFluid && invalid)}
        <button
          type="button"
          disabled="{disabled}"
          class:bx--text-input--password__visibility__toggle="{true}"
          class:bx--btn="{true}"
          class:bx--btn--icon-only="{true}"
          class:bx--btn--disabled="{disabled}"
          class:bx--tooltip__trigger="{true}"
          class:bx--tooltip--a11y="{true}"
          class:bx--tooltip--top="{tooltipPosition === 'top'}"
          class:bx--tooltip--right="{tooltipPosition === 'right'}"
          class:bx--tooltip--bottom="{tooltipPosition === 'bottom'}"
          class:bx--tooltip--left="{tooltipPosition === 'left'}"
          class:bx--tooltip--align-start="{tooltipAlignment === 'start'}"
          class:bx--tooltip--align-center="{tooltipAlignment === 'center'}"
          class:bx--tooltip--align-end="{tooltipAlignment === 'end'}"
          on:click="{() => {
            showPassword = !showPassword;
          }}"
        >
          {#if !disabled}
            <span class:bx--assistive-text="{true}">
              {#if showPassword}
                {hidePasswordLabel}
              {:else}{showPasswordLabel}{/if}
            </span>
          {/if}
          {#if showPassword}
            <ViewOff class="bx--icon-visibility-off" />
          {:else}
            <View class="bx--icon-visibility-on" />
          {/if}
        </button>
      {/if}
    </div>
    {#if !isFluid && invalid}
      <p class:bx--form-requirement="{true}" id="{errorId}">
        {invalidText}
      </p>
    {/if}
    {#if !invalid && !isFluid && !inline && description}
      <p
        class:bx--form__helper-text="{true}"
        class:bx--form__helper-text--disabled="{disabled}"
        class:bx--form__helper-text--inline="{inline}"
      >
        {description}
      </p>
    {/if}
  </div>
</div>

<style>
  .bx--btn {
    color: var(--pax-black-inactive) ;
  }
  .bx--password-input:not(.bx--text-input--sm) {
    height: auto;
  }
  .bx--text-input-wrapper .bx--text-input {
    padding: var(--s);
    border: solid var(--bw) var(--pax-black-inactive);
    border-radius: var(--br);
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    width: 100%;
    text-align: left;
    background-color: var(--pax-white);
  }

  .bx--text-input-wrapper .bx--text-input:focus {
    border: solid var(--bw) var(--pax-black);
    color: var(--pax-black);
  }

  .bx--text-input:focus,
  .bx--text-input:active {
    outline: none;
  }

  .bx--text-input-wrapper .bx--text-input--invalid {
    outline: 2px solid var(--pax-orange);
  }

  .bx--text-input-wrapper .bx--text-input--invalid,
  .bx--form-requirement {
    color: var(--pax-orange);
  }

  .bx--btn:focus,
  .bx--btn:active {
    outline: none;
    background-color: transparent;
  }

  input::placeholder {
    color: var(--pax-black-inactive);
  }

  p {
    padding-left: var(--xs);
  }

  .bx--text-input-wrapper .bx--label--inline {
    display: flex;
    flex: 1;
    height: 2.5rem;
    align-items: center;
    margin: 0;
  }

  .bx--label--inline--sm {
    height: 2rem;
  }

  .bx--label--inline--xl {
    height: 3rem;
  }
</style>
